import {
  runCrossWorkspaceFileDropWorkflow,
  runWorkspaceAddModeWorkflow,
} from "@/action-dialog";
import { useCanvasById } from "@/api/canvas";
import {
  useCopyFileRecord,
  useCreateFileRecordReference,
} from "@/api/fileRecords";
import { useFileConflictResolver } from "@/components/FileConflictResolver";
import { useCanvas } from "@/pages/canvas/context/CanvasContext";
import { useCanvasNodePlacement } from "@/pages/canvas/hooks/useCanvasNodePlacement";
import { buildFileRecordEntityForCanvas } from "@/pages/canvas/utils/imageDimensions";
import type { FileRecord } from "@cohesive/api-client";
import type { Node } from "@xyflow/react";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";

/**
 * The canvas "Add from workspace" flow behind the nested-pop picker. Picking a
 * file asks reference-or-copy first — the same fork drops go through:
 * same-workspace picks get the reference-default chooser, cross-workspace
 * picks the copy-default dialog with the access caveat. The resulting node
 * lands at the click's flow position (right-click invocations) or the next
 * free spot (chrome invocations).
 */
export function useAddFromWorkspaceToCanvas(canvasId: string | undefined) {
  const { data: canvas } = useCanvasById(canvasId ?? "");
  const { addNode } = useCanvas();
  const { getNextNodePosition } = useCanvasNodePlacement();
  const createReference = useCreateFileRecordReference();
  const copyFileRecord = useCopyFileRecord(undefined);
  const { resolveCopies } = useFileConflictResolver();
  const workspaceId = canvas?.workspace_id;

  const onPick = useCallback(
    (record: FileRecord, at?: { x: number; y: number }) => {
      if (!canvasId) return;
      // Deferred past the closing picker menu so the chooser dialog doesn't
      // fight Radix's unmount focus handling.
      window.setTimeout(() => {
        void (async () => {
          const fileName = record.path.split("/").pop() || record.path;
          try {
            const choice =
              record.workspace_id &&
              workspaceId &&
              record.workspace_id !== workspaceId
                ? await runCrossWorkspaceFileDropWorkflow({
                    count: 1,
                    fileName,
                    destination: "canvas",
                  })
                : await runWorkspaceAddModeWorkflow({
                    count: 1,
                    fileName,
                    destination: "canvas",
                  });
            if (!choice) return;

            let placedRecord: FileRecord;
            let toastMessage: string;
            if (choice === "reference") {
              const res = await createReference.mutateAsync({
                fileRecordId: record.id,
                payload: { canvas_id: canvasId },
              });
              placedRecord = res.file_record;
              toastMessage = res.existing
                ? "Already on this canvas"
                : "Added from the workspace";
            } else {
              const ops = await resolveCopies({
                copies: [
                  {
                    fileRecordId: record.id,
                    sourceName: fileName,
                    isFolder: false,
                    source: {
                      class: "workspace",
                      workspace_id: record.workspace_id,
                      path: record.path,
                    },
                    destination: {
                      components: { class: "canvas", canvas_id: canvasId },
                      parentPath: "",
                    },
                  },
                ],
                sourceRecords: [record],
              });
              // Null or empty = the conflict prompt was cancelled/skipped.
              if (!ops || ops.length === 0) return;
              placedRecord = await copyFileRecord.mutateAsync({
                fileRecordId: ops[0].sourceFileRecordId,
                payload: ops[0].payload,
              });
              toastMessage = "Added a copy from the workspace";
            }

            const { entity, nodeWidth, nodeHeight, blockMarkers } =
              buildFileRecordEntityForCanvas({
                record: placedRecord,
                contentType: placedRecord.content_type,
              });
            addNode({
              id: placedRecord.id,
              type: "entity",
              // Invoked from the board's right-click menu, the node lands at
              // the click's flow position; chrome invocations (toolbar) take
              // the next free spot.
              position: at ?? getNextNodePosition(nodeWidth, nodeHeight),
              width: nodeWidth,
              height: nodeHeight,
              data: { entity, ...blockMarkers },
            } as Node & Record<string, unknown>);
            toast.success(toastMessage);
          } catch {
            toast.error("Failed to add from the workspace");
          }
        })();
      }, 0);
    },
    [
      addNode,
      canvasId,
      copyFileRecord,
      createReference,
      getNextNodePosition,
      resolveCopies,
      workspaceId,
    ],
  );

  return useMemo(
    () => (workspaceId ? { workspaceId, onPick } : undefined),
    [onPick, workspaceId],
  );
}
